/**
 * Product / Lifestyle image view toggle.
 *
 * Writes `data-image-view="product|lifestyle"` onto <html> so card CSS can swap
 * each card's slideshow for its `custom.lifestyle_image` metafield photo.
 *
 * <html> is used deliberately: the product grid <ul> is replaced wholesale when
 * filters change or infinite scroll appends a page, so state stored on it would
 * be lost. <html> survives everything short of a navigation.
 */

const STORAGE_PREFIX = 'product-image-view';
const LARGE_VIEWPORT = window.matchMedia('(min-width: 750px)');

const currentViewport = () => (LARGE_VIEWPORT.matches ? 'desktop' : 'mobile');

// How far outside the viewport to warm images, in screens.
const WARM_MARGIN_SCREENS = 1.5;

/**
 * Force-loads the images belonging to `view` on cards at or near the viewport.
 *
 * Both image sets ship lazy, and the inactive one is display:none — so a lazy
 * image in it never intersects and never loads. That is what we want on first
 * paint, but it means an un-warmed toggle would blank every card while the new
 * set downloads. Warming on intent (hover / touch / focus) covers the gap, and
 * because it runs for whichever set is *not* active it makes toggling back just
 * as instant as toggling forward.
 */
function warmView(view) {
  const cards = document.querySelectorAll('.product-grid__item');
  if (!cards.length) return;

  const limit = window.innerHeight * (1 + WARM_MARGIN_SCREENS);

  for (const card of cards) {
    const box = card.getBoundingClientRect();
    if (box.top > limit) break;
    if (box.bottom < 0) continue;

    const image =
      view === 'lifestyle'
        ? card.querySelector('.card-gallery__lifestyle-image')
        : card.querySelector('.product-media__image');

    if (!image || image.dataset.viewWarmed === 'true') continue;

    // `sizes="auto"` is only valid on a lazy image; leaving it in place while
    // switching to eager invalidates the whole list and falls back to 100vw.
    const sizes = image.getAttribute('sizes');
    if (sizes) image.setAttribute('sizes', sizes.replace(/^auto\s*,\s*/, ''));

    image.setAttribute('loading', 'eager');
    image.dataset.viewWarmed = 'true';
  }
}

/**
 * The toggle is meaningless unless some product in the grid actually carries a
 * `custom.lifestyle_image`. It is hidden by default in CSS and revealed here, so
 * a partially-populated catalogue never shows a control that does nothing.
 */
function updateToggleAvailability() {
  const hasLifestyle = document.querySelector('.card-gallery__lifestyle') !== null;
  document.documentElement.toggleAttribute('data-has-lifestyle', hasLifestyle);
}

function readStored() {
  try {
    return sessionStorage.getItem(`${STORAGE_PREFIX}-${currentViewport()}`) === 'lifestyle'
      ? 'lifestyle'
      : 'product';
  } catch {
    // Private mode / blocked storage: fall back to the default view.
    return 'product';
  }
}

function apply(value, persist, userInitiated) {
  const view = value === 'lifestyle' ? 'lifestyle' : 'product';

  // Only user-initiated switches crossfade. Without this gate every card would
  // animate on page load, since the initial apply() also goes through here.
  if (userInitiated) {
    document.documentElement.setAttribute('data-image-view-transition', '');
    // Warm the set we are leaving so toggling straight back is instant too.
    warmView(view === 'lifestyle' ? 'product' : 'lifestyle');
  }

  document.documentElement.setAttribute('data-image-view', view);

  // Re-check every toggle instance (mobile bar + desktop toolbar) so they agree.
  const inputs = document.querySelectorAll('input[data-image-view-option]');
  for (const input of inputs) {
    const shouldCheck = input.value === view;
    if (input.checked !== shouldCheck) input.checked = shouldCheck;
  }

  if (persist) {
    try {
      sessionStorage.setItem(`${STORAGE_PREFIX}-${currentViewport()}`, view);
    } catch {
      /* storage unavailable — the view still applies for this page */
    }
  }
}

// Delegated so it keeps working after the toolbar is re-rendered.
document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement)) return;
  if (!target.hasAttribute('data-image-view-option')) return;
  apply(target.value, true, true);
});

// Warm on intent: by the time the shopper commits, the images are already in
// flight, so the swap lands without cards blanking. Delegated for the same
// reason as `change`, and passive so it never delays scrolling.
const warmFromIntent = (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const option = target.closest('[data-image-view-option], .image-view-toggle__option');
  if (!option) return;

  const input = option.matches('[data-image-view-option]')
    ? option
    : option.querySelector('[data-image-view-option]');

  if (input instanceof HTMLInputElement) warmView(input.value);
};

for (const type of ['pointerenter', 'touchstart', 'focusin']) {
  document.addEventListener(type, warmFromIntent, { passive: true, capture: true });
}

// Crossing the 750px breakpoint switches to that viewport's remembered view.
LARGE_VIEWPORT.addEventListener('change', () => apply(readStored(), false));

// Re-assert state after the grid or toolbar is swapped out by facets.js /
// results-list.js. Only `checked` properties are written, and the <html>
// attribute lives outside the observed subtree, so this cannot self-trigger.
let queued = false;
const resync = new MutationObserver(() => {
  if (queued) return;
  queued = true;
  requestAnimationFrame(() => {
    queued = false;
    apply(readStored(), false);
    updateToggleAvailability();
    // The grid may have just been replaced by a filter/sort re-render, which
    // drops the eager + high-priority hints the inline script set on first load.
    if (typeof window.__promoteGridImages === 'function') window.__promoteGridImages();
  });
});

function start() {
  apply(readStored(), false);
  updateToggleAvailability();
  resync.observe(document.body, { childList: true, subtree: true });
}

apply(readStored(), false);

if (document.body) {
  start();
} else {
  document.addEventListener('DOMContentLoaded', start, { once: true });
}
